import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Calculator,
  Sparkles,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Clock,
  Printer,
  ShieldCheck,
  RefreshCw,
  Eye,
  FileText,
  Sliders,
  RotateCcw,
  Check,
  X,
  Plus,
  HelpCircle,
  FileCheck2,
  History,
  Award,
  Search,
  Layers,
  FileSpreadsheet,
  Download,
  AlertTriangle
} from 'lucide-react';

export const MathSolutionPage: React.FC = () => {
  const { fetchWithAuth, user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'principal';

  type TabType =
    | 'overview'
    | 'answer-key'
    | 'solutions'
    | 'validator'
    | 'marking-scheme'
    | 'paper-package'
    | 'errors';

  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Global Filters
  const [selectedBoard, setSelectedBoard] = useState<number>(2); // 1: HBSE, 2: CBSE
  const [selectedClass, setSelectedClass] = useState<number>(1); // 1: Class 10, 2: Class 12
  const [selectedMedium, setSelectedMedium] = useState<string>('English');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Data States
  const [loading, setLoading] = useState(false);
  const [coverage, setCoverage] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [answerKey, setAnswerKey] = useState<any>(null);
  const [solutions, setSolutions] = useState<any[]>([]);
  const [markingScheme, setMarkingScheme] = useState<any>(null);
  const [errors, setErrors] = useState<any[]>([]);
  const [validationResult, setValidationResult] = useState<any>(null);

  // Solution Generation Form States
  const [genMethod, setGenMethod] = useState<string>('STANDARD');
  const [genLanguage, setGenLanguage] = useState<string>('English');
  const [genFinalAnswer, setGenFinalAnswer] = useState<string>('');

  // Error Report Modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorType, setErrorType] = useState('CALCULATION_ERROR');
  const [errorDetails, setErrorDetails] = useState('');
  const [errorStatusMsg, setErrorStatusMsg] = useState<{ success: boolean; text: string } | null>(null);

  // Paper Package States
  const [paperIdInput, setPaperIdInput] = useState<string>('');
  const [paperPackageResult, setPaperPackageResult] = useState<any>(null);
  const [paperSolutionsResult, setPaperSolutionsResult] = useState<any>(null);

  // Load Coverage & Question List
  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Solution Coverage
      const covRes = await fetchWithAuth(`/api/math/solution-coverage?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const covData = await covRes.json();
      if (covData.success) setCoverage(covData);

      // 2. Questions List
      const qRes = await fetchWithAuth(`/api/pyq-intelligence/questions?board_id=${selectedBoard}&class_id=${selectedClass}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`);
      const qData = await qRes.json();
      if (qData.success && qData.questions) {
        setQuestions(qData.questions);
        if (qData.questions.length > 0 && !selectedQuestion) {
          selectQuestionDetail(qData.questions[0]);
        }
      }

      // 3. Solution Errors
      const errRes = await fetchWithAuth('/api/math/solution-errors');
      const errData = await errRes.json();
      if (errData.success) setErrors(errData.errors || []);
    } catch (err) {
      console.error('Failed to load solution data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedBoard, selectedClass]);

  // Select a question to inspect Answer, Solution & Marking Scheme
  const selectQuestionDetail = async (q: any) => {
    setSelectedQuestion(q);
    setValidationResult(null);
    try {
      // Answer Key
      const aRes = await fetchWithAuth(`/api/math/questions/${q.id}/answer`);
      const aData = await aRes.json();
      if (aData.success) setAnswerKey(aData.answer);

      // Solutions
      const sRes = await fetchWithAuth(`/api/math/questions/${q.id}/solutions`);
      const sData = await sRes.json();
      if (sData.success) setSolutions(sData.solutions || []);

      // Marking Scheme
      const mRes = await fetchWithAuth(`/api/math/marking-schemes/${q.id}`);
      const mData = await mRes.json();
      if (mData.success) setMarkingScheme(mData.marking_scheme);
    } catch (err) {
      console.error('Failed to load question details:', err);
    }
  };

  // Generate Step-by-Step Solution
  const handleGenerateSolution = async () => {
    if (!selectedQuestion) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/math/solutions/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: selectedQuestion.id,
          method: genMethod,
          language: genLanguage,
          final_answer: genFinalAnswer || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        selectQuestionDetail(selectedQuestion);
      } else {
        alert(data.message || 'Generation failed');
      }
    } catch (err) {
      console.error('Error generating solution:', err);
    } finally {
      setLoading(false);
    }
  };

  // Validate Solution Mathematically
  const handleValidateSolution = async (solId: number) => {
    try {
      const res = await fetchWithAuth(`/api/math/solutions/${solId}/validate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setValidationResult(data);
        selectQuestionDetail(selectedQuestion);
      }
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  // Verify Solution (Admin)
  const handleVerifySolution = async (solId: number) => {
    try {
      const res = await fetchWithAuth(`/api/math/solutions/${solId}/verify`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert('Solution officially verified!');
        selectQuestionDetail(selectedQuestion);
        loadData();
      }
    } catch (err) {
      console.error('Verification error:', err);
    }
  };

  // Submit Error Report
  const handleSubmitError = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuestion || !errorDetails) return;
    try {
      const res = await fetchWithAuth('/api/math/solution-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: selectedQuestion.id,
          solution_id: solutions.length > 0 ? solutions[0].id : null,
          error_type: errorType,
          details: errorDetails
        })
      });
      const data = await res.json();
      if (data.success) {
        setErrorStatusMsg({ success: true, text: 'Error reported successfully for Admin review.' });
        setErrorDetails('');
        loadData();
        setTimeout(() => setShowErrorModal(false), 1500);
      }
    } catch (err: any) {
      setErrorStatusMsg({ success: false, text: err.message || 'Submission failed' });
    }
  };

  // Generate Paper Answer Key & Solutions
  const handleGeneratePaperPackage = async () => {
    if (!paperIdInput) {
      alert('Please enter a Generated Paper ID');
      return;
    }
    setLoading(true);
    try {
      // 1. Answer Key
      const akRes = await fetchWithAuth(`/api/math/papers/${paperIdInput}/generate-answer-key`, { method: 'POST' });
      const akData = await akRes.json();
      if (akData.success) setPaperPackageResult(akData);

      // 2. Solutions
      const solRes = await fetchWithAuth(`/api/math/papers/${paperIdInput}/generate-solutions`, { method: 'POST' });
      const solData = await solRes.json();
      if (solData.success) setPaperSolutionsResult(solData);
    } catch (err) {
      console.error('Paper package error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#1E40AF] text-white p-6 rounded-2xl shadow-md border-l-4 border-amber-400">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-amber-300 text-xs font-black uppercase tracking-wider">
              <Calculator className="w-4 h-4" />
              <span>Phase 20 Production Engine • District Nuh, Haryana</span>
            </div>
            <h1 className="text-xl md:text-2xl font-black text-white mt-1">
              Complete Real PYQ Corpus, Answer Key & Solution Engine
            </h1>
            <p className="text-xs text-slate-200 mt-1 max-w-3xl font-medium">
              Verified step-by-step mathematical derivations, 10-point arithmetic validators, side-by-side Answer-Solution consistency, official marking schemes, and complete downloadable Teacher Solution Packages.
            </p>
          </div>

          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-xs p-2 rounded-xl border border-white/20">
            <button
              onClick={() => setSelectedBoard(1)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${
                selectedBoard === 1 ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-white hover:bg-white/10'
              }`}
            >
              HBSE (Haryana)
            </button>
            <button
              onClick={() => setSelectedBoard(2)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${
                selectedBoard === 2 ? 'bg-amber-400 text-slate-950 shadow-md' : 'text-white hover:bg-white/10'
              }`}
            >
              CBSE (Central)
            </button>
          </div>
        </div>
      </div>

      {/* Global Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Class:</span>
            <select
              value={selectedClass}
              onChange={e => setSelectedClass(Number(e.target.value))}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value={1}>Class 10 (Secondary)</option>
              <option value={2}>Class 12 (Senior Secondary)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Medium:</span>
            <select
              value={selectedMedium}
              onChange={e => setSelectedMedium(e.target.value)}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value="English">English Medium</option>
              <option value="Hindi">Hindi Medium (हिंदी)</option>
              <option value="Bilingual">Bilingual</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-bold flex items-center gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 7-Tab Navigation Bar */}
      <div className="flex overflow-x-auto border-b border-slate-200 bg-white rounded-t-xl p-1 gap-1 text-xs font-bold shadow-xs">
        {[
          { id: 'overview', label: '1. Question & Corpus', icon: BookOpen, color: 'text-amber-400' },
          { id: 'answer-key', label: '2. Answer Key', icon: CheckCircle2, color: 'text-emerald-400' },
          { id: 'solutions', label: '3. Step Solutions', icon: Calculator, color: 'text-blue-400' },
          { id: 'validator', label: '4. Mathematical Validator', icon: ShieldCheck, color: 'text-purple-400' },
          { id: 'marking-scheme', label: '5. Marking Scheme', icon: Award, color: 'text-orange-400' },
          { id: 'paper-package', label: '6. Paper Solution Package', icon: Sparkles, color: 'text-indigo-400' },
          { id: 'errors', label: '7. Error Review Queue', icon: AlertTriangle, color: 'text-rose-400' }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
                isActive ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${tab.color}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ----------------- TAB 1: QUESTION & CORPUS OVERVIEW ----------------- */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Solution Coverage KPIs */}
          {coverage && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-1">
                <div className="text-slate-500 font-bold text-xs uppercase">Total PYQs</div>
                <div className="text-2xl font-black text-slate-900">{coverage.total_questions}</div>
                <div className="text-[10px] text-slate-500 font-bold">100% Real Board PYQs</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-emerald-200 bg-emerald-50/30 shadow-xs space-y-1">
                <div className="text-emerald-800 font-bold text-xs uppercase">Answered</div>
                <div className="text-2xl font-black text-emerald-950">{coverage.answered}</div>
                <div className="text-[10px] text-emerald-700 font-bold">With Active Key</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-blue-200 bg-blue-50/30 shadow-xs space-y-1">
                <div className="text-blue-800 font-bold text-xs uppercase">Step Solved</div>
                <div className="text-2xl font-black text-blue-950">{coverage.solved}</div>
                <div className="text-[10px] text-blue-700 font-bold">Full Derivations</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-purple-200 bg-purple-50/30 shadow-xs space-y-1">
                <div className="text-purple-800 font-bold text-xs uppercase">Verified</div>
                <div className="text-2xl font-black text-purple-950">{coverage.verified}</div>
                <div className="text-[10px] text-purple-700 font-bold">Official / Admin Key</div>
              </div>

              <div className="p-4 bg-white rounded-2xl border border-rose-200 bg-rose-50/30 shadow-xs space-y-1">
                <div className="text-rose-800 font-bold text-xs uppercase">Pending / Errors</div>
                <div className="text-2xl font-black text-rose-950">{coverage.errors}</div>
                <div className="text-[10px] text-rose-700 font-bold">In Review Queue</div>
              </div>
            </div>
          )}

          {/* Master Questions Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Question Selector */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3">
              <div className="font-black text-slate-900 text-sm border-b pb-2">Select Question from Corpus</div>
              <div className="space-y-2 max-h-[550px] overflow-y-auto pr-1">
                {questions.map((q) => (
                  <div
                    key={q.id}
                    onClick={() => selectQuestionDetail(q)}
                    className={`p-3 rounded-xl border transition cursor-pointer text-xs space-y-1 ${
                      selectedQuestion?.id === q.id
                        ? 'bg-blue-50 border-blue-500 shadow-xs'
                        : 'bg-slate-50 hover:bg-slate-100 border-slate-200'
                    }`}
                  >
                    <div className="flex justify-between items-center text-[10px]">
                      <span className="font-bold text-slate-900">Q{q.question_number} • {q.marks}M</span>
                      <span className="text-slate-500 font-mono">{q.set_name} ({q.year})</span>
                    </div>
                    <div className="font-medium text-slate-800 line-clamp-2">{q.question_text_normalized}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Unified Question 360 View */}
            {selectedQuestion && (
              <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
                <div className="flex justify-between items-start border-b pb-3">
                  <div>
                    <h3 className="text-base font-black text-slate-900">
                      Question Detail (ID: {selectedQuestion.id})
                    </h3>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {selectedBoard === 1 ? 'HBSE' : 'CBSE'} Class {selectedClass === 1 ? '10' : '12'} • {selectedQuestion.set_name} • {selectedQuestion.year}
                    </div>
                  </div>

                  <button
                    onClick={() => setShowErrorModal(true)}
                    className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-900 border border-rose-200 rounded-lg text-xs font-bold flex items-center gap-1"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                    <span>Report Error</span>
                  </button>
                </div>

                {/* Original Question Card */}
                <div className="p-4 bg-slate-50 rounded-xl border space-y-1 text-xs">
                  <div className="text-slate-500 font-bold uppercase text-[10px]">Original Question Text:</div>
                  <div className="font-medium text-slate-900">{selectedQuestion.question_text_original}</div>
                </div>

                {/* Normalized Mathematical Text */}
                <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-200 space-y-1 text-xs">
                  <div className="text-emerald-800 font-bold uppercase text-[10px]">Normalized Mathematics (Unicode & KaTeX):</div>
                  <div className="font-bold text-emerald-950 font-mono text-sm">{selectedQuestion.question_text_normalized}</div>
                </div>

                {/* Active Answer Key Preview */}
                {answerKey && (
                  <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-200 space-y-1 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-indigo-900 font-bold uppercase text-[10px]">Answer Key ({answerKey.verification_status}):</span>
                      <span className="px-2 py-0.5 bg-indigo-200 text-indigo-950 rounded font-mono text-[10px] font-bold">
                        {answerKey.answer_type}
                      </span>
                    </div>
                    <div className="font-black text-indigo-950 text-base">{answerKey.final_answer}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ----------------- TAB 2: ANSWER KEY ----------------- */}
      {activeTab === 'answer-key' && selectedQuestion && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Official & Verified Answer Key (Question ID: {selectedQuestion.id})
            </h3>
            <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-900 rounded font-bold text-[10px]">
              {answerKey?.verification_status || 'ADMIN_VERIFIED'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl border space-y-2">
              <div className="text-slate-500 font-bold uppercase text-[10px]">Question:</div>
              <div className="font-medium text-slate-900">{selectedQuestion.question_text_normalized}</div>
            </div>

            <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-2">
              <div className="text-emerald-800 font-bold uppercase text-[10px]">Official Final Answer:</div>
              <div className="text-xl font-black text-emerald-950 font-mono">{answerKey?.final_answer || 'Pending'}</div>
              <div className="text-[11px] text-slate-600">{answerKey?.answer_text}</div>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- TAB 3: STEP-BY-STEP SOLUTIONS ----------------- */}
      {activeTab === 'solutions' && selectedQuestion && (
        <div className="space-y-6">
          {/* Solution Generator Card */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-black text-slate-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                Generate Step-by-Step Mathematical Derivation
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Method Style</label>
                <select
                  value={genMethod}
                  onChange={e => setGenMethod(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="STANDARD">Standard Board Method</option>
                  <option value="BOARD_STYLE">Board Step-Marking Method</option>
                  <option value="DETAILED">Detailed Theoretical Method</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Language</label>
                <select
                  value={genLanguage}
                  onChange={e => setGenLanguage(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi (हिंदी)</option>
                  <option value="Bilingual">Bilingual (Hindi + English)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Final Answer Override (Optional)</label>
                <input
                  type="text"
                  value={genFinalAnswer}
                  onChange={e => setGenFinalAnswer(e.target.value)}
                  placeholder="e.g. x = 2, x = 3"
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={handleGenerateSolution}
                disabled={loading}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black shadow-xs"
              >
                Generate Derivation
              </button>
            </div>
          </div>

          {/* Solutions List */}
          <div className="space-y-4">
            {solutions.map((sol) => (
              <div key={sol.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 text-xs font-sans">
                <div className="flex justify-between items-center border-b pb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-slate-900 text-sm">{sol.method_name}</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded font-bold text-[10px]">
                      Version {sol.solution_version}
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 rounded font-bold text-[10px]">
                      {sol.verification_status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleValidateSolution(sol.id)}
                      className="px-3 py-1 bg-purple-50 hover:bg-purple-100 text-purple-900 border border-purple-200 rounded-lg font-bold"
                    >
                      Run Validator
                    </button>

                    {isAdmin && sol.verification_status !== 'OFFICIAL_VERIFIED' && (
                      <button
                        onClick={() => handleVerifySolution(sol.id)}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold shadow-xs"
                      >
                        Verify Official
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {sol.steps?.map((step: any) => (
                    <div key={step.step_number} className="p-3 bg-slate-50 rounded-xl border space-y-1">
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="font-bold text-slate-700 uppercase">Step {step.step_number}</span>
                      </div>
                      <div className="font-medium text-slate-900">{step.description}</div>
                      {step.formula && (
                        <div className="font-mono text-indigo-900 font-bold bg-indigo-50/50 p-1.5 rounded text-[11px]">
                          {step.formula}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 flex justify-between items-center">
                  <span className="font-bold text-emerald-900 uppercase text-[10px]">Final Derivation Result:</span>
                  <span className="font-black text-emerald-950 font-mono text-sm">{sol.final_answer}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 4: MATHEMATICAL VALIDATOR ----------------- */}
      {activeTab === 'validator' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-purple-600" />
              10-Point Mathematical Validation & Mismatch Detection
            </h3>
            {validationResult && (
              <span className={`px-3 py-1 rounded font-bold text-xs ${
                validationResult.status === 'ADMIN_VERIFIED' ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900'
              }`}>
                Score: {validationResult.quality_score}% ({validationResult.status})
              </span>
            )}
          </div>

          {validationResult ? (
            <div className="space-y-3">
              <div className="divide-y divide-slate-100">
                {validationResult.checks?.map((c: any, idx: number) => (
                  <div key={idx} className="py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {c.pass ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-rose-600" />}
                      <span className="font-bold text-slate-800">{c.name}</span>
                    </div>
                    <span className="text-slate-500 font-mono text-[11px]">{c.details}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 font-medium">
              Select a solution from Tab 3 and click "Run Validator" to execute the 10-point check.
            </div>
          )}
        </div>
      )}

      {/* ----------------- TAB 5: MARKING SCHEME ----------------- */}
      {activeTab === 'marking-scheme' && selectedQuestion && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <Award className="w-4 h-4 text-orange-600" />
              Official Step-Wise Marking Scheme (Total: {selectedQuestion.marks} Mark(s))
            </h3>
            <span className="px-2.5 py-0.5 bg-orange-100 text-orange-950 font-bold rounded text-[10px]">
              {markingScheme?.official_status || 'ADMIN_VERIFIED'}
            </span>
          </div>

          <div className="space-y-2">
            {markingScheme?.criteria?.map((item: any, idx: number) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-xl border flex items-center justify-between">
                <span className="font-medium text-slate-900">{item.step}</span>
                <span className="px-2.5 py-1 bg-orange-500 text-white rounded font-mono font-bold text-[11px]">
                  {item.marks} Mark(s)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 6: PAPER SOLUTION PACKAGE & PDF EXPORT ----------------- */}
      {activeTab === 'paper-package' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border-2 border-indigo-200 shadow-md space-y-4 text-xs">
            <div className="flex justify-between items-center border-b pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-700" />
                <h3 className="text-base font-black text-slate-900">Complete Paper Solution Package Generator</h3>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block font-bold text-slate-700 mb-1">Enter Generated Paper ID</label>
                <input
                  type="number"
                  value={paperIdInput}
                  onChange={e => setPaperIdInput(e.target.value)}
                  placeholder="e.g. 118"
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                />
              </div>

              <div className="pt-5">
                <button
                  onClick={handleGeneratePaperPackage}
                  disabled={loading}
                  className="px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-black rounded-xl shadow-md"
                >
                  Generate Complete Package
                </button>
              </div>
            </div>
          </div>

          {/* Solution Package Layout & Print */}
          {paperSolutionsResult && (
            <div className="bg-white p-8 rounded-2xl border-2 border-slate-300 shadow-lg space-y-6 font-serif text-slate-900">
              <div className="flex justify-between items-center border-b pb-4 not-italic font-sans">
                <div>
                  <h3 className="font-black text-base text-slate-900">
                    Complete Teacher Package (Paper ID: {paperSolutionsResult.paper_id})
                  </h3>
                  <div className="text-xs text-slate-500">Includes Question Paper + Answer Key + Detailed Step Derivations</div>
                </div>

                <button
                  onClick={() => window.print()}
                  className="px-4 py-1.5 bg-slate-900 hover:bg-black text-white rounded-lg text-xs font-bold flex items-center gap-1"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print Complete Package (A4)</span>
                </button>
              </div>

              <div className="space-y-6">
                {paperSolutionsResult.solutions?.map((item: any) => (
                  <div key={item.item_number} className="p-4 bg-slate-50 rounded-xl border space-y-2 text-xs font-sans">
                    <div className="flex justify-between items-center font-bold">
                      <span className="text-slate-900 text-sm">Q{item.item_number}. ({item.section_name})</span>
                      <span className="font-mono text-slate-600">[{item.marks} Mark(s)]</span>
                    </div>
                    <div className="text-slate-800 font-medium">{item.question_text}</div>

                    <div className="mt-2 space-y-1.5 pt-2 border-t">
                      {item.steps?.map((step: any) => (
                        <div key={step.step_number} className="p-2 bg-white rounded border space-y-0.5">
                          <div className="text-[10px] font-bold text-slate-500">Step {step.step_number}:</div>
                          <div className="text-slate-800">{step.description}</div>
                          {step.formula && <div className="font-mono text-indigo-900 text-[10px]">{step.formula}</div>}
                        </div>
                      ))}
                    </div>

                    <div className="p-2 bg-emerald-100/60 rounded font-mono font-bold text-emerald-950 flex justify-between">
                      <span>Final Answer:</span>
                      <span>{item.final_answer}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ----------------- TAB 7: ERROR REVIEW QUEUE ----------------- */}
      {activeTab === 'errors' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              Solution Error Reports Queue ({errors.length} Reported)
            </h3>
          </div>

          <div className="divide-y divide-slate-100">
            {errors.map((err) => (
              <div key={err.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900">{err.error_type}: {err.details}</div>
                  <div className="text-slate-500 text-[11px]">Reported by {err.reporter_name} • Status: {err.status}</div>
                </div>
                <span className={`px-2.5 py-0.5 rounded font-mono text-[10px] font-bold ${
                  err.status === 'RESOLVED' ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900'
                }`}>
                  {err.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 text-xs">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="font-black text-slate-900 text-sm">Report Solution Error</h3>
              <button onClick={() => setShowErrorModal(false)}>
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {errorStatusMsg && (
              <div className={`p-3 rounded-xl font-bold ${
                errorStatusMsg.success ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'
              }`}>
                {errorStatusMsg.text}
              </div>
            )}

            <form onSubmit={handleSubmitError} className="space-y-3">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Error Type</label>
                <select
                  value={errorType}
                  onChange={e => setErrorType(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="CALCULATION_ERROR">Calculation Error</option>
                  <option value="WRONG_ANSWER">Wrong Final Answer</option>
                  <option value="WRONG_FORMULA">Wrong Formula / Theorem</option>
                  <option value="MISSING_STEP">Missing Step</option>
                  <option value="WRONG_TRANSLATION">Translation Error</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Error Description & Proposed Correction</label>
                <textarea
                  rows={4}
                  value={errorDetails}
                  onChange={e => setErrorDetails(e.target.value)}
                  placeholder="Explain step error or provide correct calculation..."
                  className="w-full p-2.5 bg-slate-50 border rounded-xl font-medium"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowErrorModal(false)}
                  className="px-4 py-2 bg-slate-100 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black shadow-xs"
                >
                  Submit Error Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default MathSolutionPage;
